import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Mail, Phone, Calendar, User, MessageSquare, RefreshCw, LogOut, 
  Download, TrendingUp, Filter, Search, X, Clock, CheckCircle,
  AlertCircle, Repeat
} from 'lucide-react';
import { toast } from 'sonner';
import { exportToCSV, formatContactForCSV, formatAppointmentForCSV } from '../utils/csvExport';
import { ActivityChart } from './dashboard/ActivityChart';
import { ProjectTypeChart } from './dashboard/ProjectTypeChart';
import { UpcomingAppointments } from './dashboard/UpcomingAppointments';
import { RecentActivity } from './dashboard/RecentActivity';
import { SubscriptionsManager } from './SubscriptionsManager';
import { ConfigurationsManager } from './ConfigurationsManager';
import { AdminForbidden } from './AdminForbidden';
import { VisitsPanel } from './dashboard/VisitsPanel';
import { subDays, format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Admin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('tous');
  const [dateFilter, setDateFilter] = useState('tous');
  const [activeTab, setActiveTab] = useState('overview'); // overview, contacts, appointments, subscriptions
  
  // Stats
  const [stats, setStats] = useState({
    totalContacts: 0,
    totalAppointments: 0,
    contactsThisMonth: 0,
    appointmentsThisMonth: 0,
    contactsLastMonth: 0,
    appointmentsLastMonth: 0,
    avgResponseTime: 0,
    newContactsToday: 0
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Not authenticated');
      }
      
      const userData = await response.json();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      navigate('/login');
    }
  }, [navigate]);

  const calculateStats = useCallback((contactsData, appointmentsData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const lastMonth = subMonths(now, 1);
    const today = new Date().setHours(0, 0, 0, 0);

    const contactsThisMonth = contactsData.filter(c => {
      const date = new Date(c.timestamp);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });

    const contactsLastMonth = contactsData.filter(c => {
      const date = new Date(c.timestamp);
      return date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
    });

    const appointmentsThisMonth = appointmentsData.filter(a => {
      const date = new Date(a.timestamp);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });

    const appointmentsLastMonth = appointmentsData.filter(a => {
      const date = new Date(a.timestamp);
      return date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
    });

    const newContactsToday = contactsData.filter(c => {
      const contactDate = new Date(c.timestamp).setHours(0, 0, 0, 0);
      return contactDate === today;
    }).length;

    setStats({
      totalContacts: contactsData.length,
      totalAppointments: appointmentsData.length,
      contactsThisMonth: contactsThisMonth.length,
      appointmentsThisMonth: appointmentsThisMonth.length,
      contactsLastMonth: contactsLastMonth.length,
      appointmentsLastMonth: appointmentsLastMonth.length,
      avgResponseTime: 0,
      newContactsToday
    });
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [contactsRes, appointmentsRes] = await Promise.all([
        fetch(`${API_URL}/api/contacts`, { credentials: 'include' }),
        fetch(`${API_URL}/api/appointments`, { credentials: 'include' })
      ]);

      if (contactsRes.status === 401 || appointmentsRes.status === 401) {
        toast.error('Session expirée. Reconnexion requise.');
        handleLogout();
        return;
      }

      if (contactsRes.ok && appointmentsRes.ok) {
        const contactsData = await contactsRes.json();
        const appointmentsData = await appointmentsRes.json();
        
        setContacts(contactsData);
        setAppointments(appointmentsData);
        calculateStats(contactsData, appointmentsData);
      } else {
        setError('Erreur lors du chargement des données');
      }
    } catch (err) {
      setError('Impossible de charger les données: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [calculateStats]);

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setUser(null);
    setIsAuthenticated(false);
    navigate('/login');
    toast.info('Déconnexion réussie');
  };

  const handleStatusChange = async (id, newStatus, type) => {
    try {
      const endpoint = type === 'contact' ? 'contacts' : 'appointments';
      const response = await fetch(`${API_URL}/api/${endpoint}/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Statut mis à jour: ${newStatus}`);
        fetchData();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur: ' + error.message);
    }
  };

  const handleExportContacts = () => {
    if (filteredContacts.length === 0) {
      toast.error('Aucune donnée à exporter');
      return;
    }
    const formatted = formatContactForCSV(filteredContacts);
    exportToCSV(formatted, `contacts_${new Date().toISOString().split('T')[0]}.xlsx`, 'Contacts');
    toast.success('Export Excel réussi !');
  };

  const handleExportAppointments = () => {
    if (filteredAppointments.length === 0) {
      toast.error('Aucune donnée à exporter');
      return;
    }
    const formatted = formatAppointmentForCSV(filteredAppointments);
    exportToCSV(formatted, `rendez-vous_${new Date().toISOString().split('T')[0]}.xlsx`, 'Rendez-vous');
    toast.success('Export Excel réussi !');
  };

  useEffect(() => {
    if (location.hash?.includes('session_id=')) {
      return;
    }
    
    if (!location.state?.user) {
      checkAuth();
    }
  }, [location.hash, location.state, checkAuth]);

  useEffect(() => {
    if (isAuthenticated && user?.is_admin) {
      fetchData();
    }
  }, [isAuthenticated, user, fetchData]);

  // Filtered data based on search and filters
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      const matchesSearch = searchTerm === '' || 
        contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contact.message.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'tous' || contact.status === statusFilter;
      
      const matchesDate = dateFilter === 'tous' || (() => {
        const contactDate = new Date(contact.timestamp);
        const now = new Date();
        if (dateFilter === 'today') {
          return contactDate.toDateString() === now.toDateString();
        } else if (dateFilter === 'week') {
          const weekAgo = subDays(now, 7);
          return contactDate >= weekAgo;
        } else if (dateFilter === 'month') {
          return contactDate.getMonth() === now.getMonth() && 
                 contactDate.getFullYear() === now.getFullYear();
        }
        return true;
      })();

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [contacts, searchTerm, statusFilter, dateFilter]);

  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      const matchesSearch = searchTerm === '' || 
        apt.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        apt.phone_number.includes(searchTerm);
      
      const matchesStatus = statusFilter === 'tous' || apt.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [appointments, searchTerm, statusFilter]);

  // Chart data
  const activityChartData = useMemo(() => {
    const days = 30;
    const data = [];
    const now = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(now, i);
      const dateStr = format(date, 'dd/MM');
      
      const dayContacts = contacts.filter(c => {
        const cDate = new Date(c.timestamp);
        return cDate.toDateString() === date.toDateString();
      }).length;

      const dayAppointments = appointments.filter(a => {
        const aDate = new Date(a.timestamp);
        return aDate.toDateString() === date.toDateString();
      }).length;

      data.push({
        date: dateStr,
        contacts: dayContacts,
        appointments: dayAppointments
      });
    }

    return data;
  }, [contacts, appointments]);

  const projectTypeChartData = useMemo(() => {
    const types = {};
    contacts.forEach(c => {
      types[c.project_type] = (types[c.project_type] || 0) + 1;
    });

    return Object.entries(types).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value
    }));
  }, [contacts]);

  // Still checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-gold animate-spin mx-auto mb-4" />
          <p className="text-white">Vérification de l&apos;authentification...</p>
        </div>
      </div>
    );
  }

  if (user && !user.is_admin) {
    return <AdminForbidden user={user} onLogout={handleLogout} />;
  }

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <TrendingUp className="w-4 h-4 text-neon" />;
    if (current < previous) return <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />;
    return null;
  };

  const getTrendText = (current, previous) => {
    if (previous === 0) return '';
    const diff = current - previous;
    const percent = Math.abs((diff / previous) * 100).toFixed(0);
    return diff > 0 ? `+${percent}%` : `-${percent}%`;
  };

  return (
    <div className="min-h-screen bg-dark py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
          <div>
            <div className="flex gap-5 mb-3 text-[11px] uppercase tracking-[0.25em]">
              <a href="/" data-testid="admin-back-to-site" className="text-gray-500 hover:text-gold transition-colors">← Retour au site</a>
              <a href="/mon-espace" data-testid="admin-client-space-link" className="text-gray-500 hover:text-gold transition-colors">Mon espace</a>
            </div>
            <h1 className="text-4xl font-playfair font-bold text-white">
              Tableau de Bord Admin
            </h1>
            {user && (
              <p className="text-gray-400 mt-2">
                Connecté en tant que <span className="text-gold">{user.name}</span>
              </p>
            )}
          </div>
          <div className="flex gap-4 items-center">
            {user?.picture && (
              <img 
                src={user.picture} 
                alt={user.name}
                className="w-10 h-10 rounded-full border-2 border-gold"
              />
            )}
            <Button
              onClick={fetchData}
              className="bg-gold hover:bg-gold-light text-dark"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-gold/30 text-gold hover:bg-gold/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gold/20 overflow-x-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'overview'
                ? 'text-gold border-b-2 border-gold'
                : 'text-gray-400 hover:text-gold'
            }`}
          >
            Vue d&apos;ensemble
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'contacts'
                ? 'text-gold border-b-2 border-gold'
                : 'text-gray-400 hover:text-gold'
            }`}
          >
            Contacts ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'appointments'
                ? 'text-gold border-b-2 border-gold'
                : 'text-gray-400 hover:text-gold'
            }`}
          >
            Rendez-vous ({appointments.length})
          </button>
          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'subscriptions'
                ? 'text-gold border-b-2 border-gold'
                : 'text-gray-400 hover:text-gold'
            }`}
          >
            <span className="flex items-center gap-2">
              <Repeat className="w-4 h-4" />
              Abonnements
            </span>
          </button>
          <button
            onClick={() => setActiveTab('configurations')}
            data-testid="admin-tab-configurations"
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'configurations'
                ? 'text-gold border-b-2 border-gold'
                : 'text-gray-400 hover:text-gold'
            }`}
          >
            Configurations
          </button>
        </div>

        {loading && (
          <div className="text-center text-white py-12">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
            <p>Chargement des données...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-sm mb-8">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                <VisitsPanel />
                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-gold/10 to-gold/5 border-gold/30">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-gray-400 text-sm mb-1">Total Contacts</p>
                        <p className="text-4xl font-bold text-white">{stats.totalContacts}</p>
                        {stats.newContactsToday > 0 && (
                          <p className="text-xs text-neon mt-2">
                            +{stats.newContactsToday} aujourd&apos;hui
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-neon/10 to-neon/5 border-neon/30">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-gray-400 text-sm mb-1">Ce Mois</p>
                        <p className="text-4xl font-bold text-neon">{stats.contactsThisMonth}</p>
                        <div className="flex items-center justify-center gap-2 mt-2">
                          {getTrendIcon(stats.contactsThisMonth, stats.contactsLastMonth)}
                          <p className="text-xs text-gray-400">
                            {getTrendText(stats.contactsThisMonth, stats.contactsLastMonth)} vs mois dernier
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-gold/10 to-gold/5 border-gold/30">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-gray-400 text-sm mb-1">Total Rendez-vous</p>
                        <p className="text-4xl font-bold text-white">{stats.totalAppointments}</p>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-neon/10 to-neon/5 border-neon/30">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-gray-400 text-sm mb-1">RDV Ce Mois</p>
                        <p className="text-4xl font-bold text-neon">{stats.appointmentsThisMonth}</p>
                        <div className="flex items-center justify-center gap-2 mt-2">
                          {getTrendIcon(stats.appointmentsThisMonth, stats.appointmentsLastMonth)}
                          <p className="text-xs text-gray-400">
                            {getTrendText(stats.appointmentsThisMonth, stats.appointmentsLastMonth)} vs mois dernier
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ActivityChart data={activityChartData} />
                  <ProjectTypeChart data={projectTypeChartData} />
                </div>

                {/* Upcoming & Recent Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <UpcomingAppointments appointments={appointments} />
                  <RecentActivity contacts={contacts} />
                </div>
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="space-y-6">
                {/* Filters & Search */}
                <Card className="bg-light/5 border-gold/20">
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Rechercher par nom, email ou message..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-dark border border-gold/20 rounded text-white placeholder-gray-500 focus:border-gold focus:outline-none"
                        />
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                          >
                            <X className="w-4 h-4 text-gray-400 hover:text-white" />
                          </button>
                        )}
                      </div>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-dark border border-gold/20 rounded text-white focus:border-gold focus:outline-none"
                      >
                        <option value="tous">Tous les statuts</option>
                        <option value="nouveau">Nouveau</option>
                        <option value="lu">Lu</option>
                        <option value="traité">Traité</option>
                      </select>
                      <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="px-4 py-2 bg-dark border border-gold/20 rounded text-white focus:border-gold focus:outline-none"
                      >
                        <option value="tous">Toutes les dates</option>
                        <option value="today">Aujourd&apos;hui</option>
                        <option value="week">Cette semaine</option>
                        <option value="month">Ce mois</option>
                      </select>
                      <Button
                        onClick={handleExportContacts}
                        variant="outline"
                        className="border-gold/30 text-gold hover:bg-gold/10"
                        disabled={filteredContacts.length === 0}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Excel ({filteredContacts.length})
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Contacts List */}
                <div className="grid grid-cols-1 gap-4">
                  {filteredContacts.length === 0 ? (
                    <Card className="bg-light/5 border-gold/20">
                      <CardContent className="py-12 text-center">
                        <p className="text-gray-400">
                          Aucun contact trouvé avec ces filtres
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    filteredContacts.map((contact) => (
                      <Card key={contact.id} className="bg-light/5 border-gold/20 hover:border-gold/40 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <User className="w-5 h-5 text-gold" />
                              <div>
                                <h3 className="text-white font-semibold text-lg">
                                  {contact.name}
                                </h3>
                                <a href={`mailto:${contact.email}`} className="text-gray-400 hover:text-gold text-sm">
                                  {contact.email}
                                </a>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <select
                                value={contact.status}
                                onChange={(e) => handleStatusChange(contact.id, e.target.value, 'contact')}
                                className={`text-xs px-3 py-1 rounded-full border ${
                                  contact.status === 'nouveau' 
                                    ? 'bg-neon/20 text-neon border-neon/50' 
                                    : contact.status === 'lu'
                                    ? 'bg-blue-500/20 text-blue-400 border-blue-500/50'
                                    : 'bg-gray-500/20 text-gray-400 border-gray-500/50'
                                }`}
                              >
                                <option value="nouveau">Nouveau</option>
                                <option value="lu">Lu</option>
                                <option value="traité">Traité</option>
                              </select>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-2 text-sm text-gray-300">
                              <Mail className="w-4 h-4 text-gold" />
                              <span>Type: <span className="text-gold">{contact.project_type}</span></span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Clock className="w-4 h-4" />
                              <span>{formatDate(contact.timestamp)}</span>
                            </div>
                          </div>
                          
                          <div className="bg-dark/50 p-4 rounded border border-gold/10">
                            <p className="text-gray-300 text-sm">
                              {contact.message}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <div className="space-y-6">
                {/* Filters & Search */}
                <Card className="bg-light/5 border-gold/20">
                  <CardContent className="pt-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Rechercher par nom ou téléphone..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 bg-dark border border-gold/20 rounded text-white placeholder-gray-500 focus:border-gold focus:outline-none"
                        />
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2"
                          >
                            <X className="w-4 h-4 text-gray-400 hover:text-white" />
                          </button>
                        )}
                      </div>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-dark border border-gold/20 rounded text-white focus:border-gold focus:outline-none"
                      >
                        <option value="tous">Tous les statuts</option>
                        <option value="confirmé">Confirmé</option>
                        <option value="annulé">Annulé</option>
                        <option value="complété">Complété</option>
                      </select>
                      <Button
                        onClick={handleExportAppointments}
                        variant="outline"
                        className="border-gold/30 text-gold hover:bg-gold/10"
                        disabled={filteredAppointments.length === 0}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export Excel ({filteredAppointments.length})
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Appointments List */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredAppointments.length === 0 ? (
                    <Card className="bg-light/5 border-gold/20 col-span-full">
                      <CardContent className="py-12 text-center">
                        <p className="text-gray-400">
                          Aucun rendez-vous trouvé avec ces filtres
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    filteredAppointments.map((apt) => (
                      <Card key={apt.id} className="bg-light/5 border-gold/20 hover:border-gold/40 transition-colors">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <User className="w-5 h-5 text-gold" />
                              <h3 className="text-white font-semibold text-lg">
                                {apt.client_name}
                              </h3>
                            </div>
                            <select
                              value={apt.status}
                              onChange={(e) => handleStatusChange(apt.id, e.target.value, 'appointment')}
                              className={`text-xs px-3 py-1 rounded-full border ${
                                apt.status === 'confirmé' 
                                  ? 'bg-neon/20 text-neon border-neon/50' 
                                  : apt.status === 'complété'
                                  ? 'bg-green-500/20 text-green-400 border-green-500/50'
                                  : 'bg-red-500/20 text-red-400 border-red-500/50'
                              }`}
                            >
                              <option value="confirmé">Confirmé</option>
                              <option value="annulé">Annulé</option>
                              <option value="complété">Complété</option>
                            </select>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-gray-300">
                              <Phone className="w-4 h-4 text-gold" />
                              <a href={`tel:${apt.phone_number}`} className="hover:text-gold">
                                {apt.phone_number}
                              </a>
                            </div>
                            
                            <div className="bg-gradient-to-r from-gold/20 to-transparent p-3 rounded border border-gold/20">
                              <div className="flex items-center gap-2 text-gold font-medium mb-1">
                                <Calendar className="w-4 h-4" />
                                <span>
                                  {new Date(apt.date).toLocaleDateString('fr-FR', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                              <div className="text-gold ml-6">
                                à {apt.time}
                              </div>
                            </div>
                            
                            <div className="text-xs text-gray-500">
                              Réservé le {formatDate(apt.timestamp)}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <SubscriptionsManager />
            )}

            {activeTab === 'configurations' && (
              <ConfigurationsManager />
            )}
          </>
        )}
      </div>
    </div>
  );
};
